// Users.jsx (cards + phosphor-react + uses revised components)
import React, { Fragment, useEffect, useMemo, useState } from "react"
import { Link } from "react-router-dom"
import { DeleteModal, Loading, TextInput } from "../../components"
import axiosInstance from "../../utils/axios/axiosConfig"
import { toast } from "sonner"
import { Menu, MenuButton, MenuItem, MenuItems, Transition } from "@headlessui/react"
import { useAccessChecker } from "../../utils/Functions/Authorization"
import { motion } from "framer-motion"
import {
  DotsThreeOutlineVertical,
  PencilSimple,
  MagnifyingGlass,
  Trash,
  User as UserIcon,
  UserPlus,
} from "phosphor-react"

function Users() {
  const { checkAccess } = useAccessChecker()

  const [loading, setLoding] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [users, setUsers] = useState([])
  const [userId, setUserId] = useState(null)
  const [done, setDone] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)

  const [q, setQ] = useState("")
  const [currentPage, setCurrentPage] = useState(1)
  const usersPerPage = 12

  useEffect(() => {
    getUsers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [done])

  const getUsers = async () => {
    try {
      setErrorMessage("")
      setLoding(true)
      const response = await axiosInstance.get("/users/search")
      const responseData = response.data
      if (responseData) setUsers(responseData.data || [])
    } catch (error) {
      const msg = error?.response?.data?.message || "Failed to fetch users"
      setErrorMessage(msg)
      toast.error(msg)
      console.error("get users failed:", error?.response?.data || error)
    } finally {
      setLoding(false)
    }
  }

  const filteredUsers = useMemo(() => {
    const term = q.trim().toLowerCase()
    if (!term) return users
    return (users || []).filter((u) => {
      const name = (u.fullName || "").toLowerCase()
      const email = (u.email || "").toLowerCase()
      const phone = (u.phone || "").toLowerCase()
      const role = (u?.role?.name || "").toLowerCase()
      const status = (u.etat || "").toLowerCase()
      return (
        name.includes(term) ||
        email.includes(term) ||
        phone.includes(term) ||
        role.includes(term) ||
        status.includes(term)
      )
    })
  }, [users, q])

  const pagesCount = Math.ceil(filteredUsers.length / usersPerPage) || 1
  const safePage = Math.min(Math.max(currentPage, 1), pagesCount)

  useEffect(() => {
    if (safePage !== currentPage) setCurrentPage(safePage)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagesCount])

  const indexOfLastUser = safePage * usersPerPage
  const indexOfFirstUser = indexOfLastUser - usersPerPage
  const currentUsers = filteredUsers.slice(indexOfFirstUser, indexOfLastUser)

  const paginate = (pageNumber) => setCurrentPage(pageNumber)

  const getStatusClass = (etat) => {
    const s = String(etat || "active").toLowerCase()
    return s === "active"
      ? "bg-green-100 text-green-800 dark:bg-green-800/20 dark:text-green-400"
      : "bg-neutral-100 text-neutral-800 dark:bg-neutral-800/20 dark:text-neutral-400"
  }

  return (
    <div className="w-full h-full">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -20 }}
        transition={{ duration: 0.3 }}
        className="flex flex-col w-full h-full px-6 py-5 bg-neutral-50/80 dark:bg-neutral-950/40"
      >
        {loading && <Loading />}

        {/* Header */}
        <div className="flex lg:justify-between gap-3 lg:items-center flex-col lg:flex-row mb-6">
          <div className="flex flex-col items-start gap-1">
            <h1 className="text-xl font-semibold text-neutral-900 dark:text-white">Users</h1>
            <p className="text-sm text-neutral-600 dark:text-neutral-400">Manage users here.</p>
          </div>

          <div className="flex flex-col lg:flex-row lg:items-center gap-3 w-full lg:w-auto">
            {/* Search (uses revised TextInput) */}
            <div className="w-full lg:w-80">
              <TextInput
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Search users..."
                icon={MagnifyingGlass}
              />
            </div>

            {/* New User Button */}
            {checkAccess("users", "create") && (
              <Link
                to="/Users/new"
                className="flex justify-center items-center px-4 py-2 text-sm font-medium text-white bg-[#2B2B35] rounded-lg hover:bg-cyan-400 focus:outline-none"
              >
                <UserPlus className="w-4 h-4 mr-2" />
                New user
              </Link>
            )}
          </div>
        </div>

        {errorMessage ? (
          <div className="rounded-2xl border border-rose-200 bg-rose-50 text-rose-700 px-4 py-3 text-sm">
            {errorMessage}
          </div>
        ) : null}

        {/* Cards */}
        <div className="flex-1 overflow-auto">
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
            {currentUsers.map((user) => (
              <div
                key={user.id}
                className="rounded-2xl border border-neutral-200/70 dark:border-neutral-800 bg-white/90 dark:bg-neutral-900/70 p-4 hover:shadow-sm transition"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-3 min-w-0">
                    <div className="h-11 w-11 rounded-full overflow-hidden bg-neutral-100 dark:bg-neutral-800 shrink-0">
                      {user.image ? (
                        <img src={user.image} alt="" className="h-full w-full object-cover" draggable={false} />
                      ) : (
                        <div className="h-full w-full flex items-center justify-center">
                          <UserIcon size={18} className="text-neutral-500" />
                        </div>
                      )}
                    </div>

                    <div className="min-w-0 text-start">
                      <div className="text-sm font-semibold text-neutral-900 dark:text-white truncate">
                        {user.fullName || "—"}
                      </div>
                      <div className="text-xs text-neutral-500 dark:text-neutral-400 truncate">
                        {user.email || "—"}
                      </div>
                    </div>
                  </div>

                  {(checkAccess("users", "update") || checkAccess("users", "delete")) && (
                    <Menu as="div" className="relative">
                      <MenuButton className="inline-flex items-center p-1 text-neutral-400 hover:text-neutral-700 dark:hover:text-neutral-200 focus:outline-none">
                        <DotsThreeOutlineVertical size={18} />
                      </MenuButton>

                      <Transition
                        as={Fragment}
                        enter="transition ease-out duration-100"
                        enterFrom="transform opacity-0 scale-95"
                        enterTo="transform opacity-100 scale-100"
                        leave="transition ease-in duration-75"
                        leaveFrom="transform opacity-100 scale-100"
                        leaveTo="transform opacity-0 scale-95"
                      >
                        <MenuItems className="absolute right-0 mt-2 w-48 rounded-2xl shadow-lg bg-white dark:bg-neutral-900 border border-neutral-200 dark:border-neutral-800 focus:outline-none overflow-hidden z-50">
                          {checkAccess("users", "update") && (
                            <MenuItem>
                              {({ active }) => (
                                <Link
                                  to={`/Users/update/${user.id}`}
                                  className={[
                                    "flex items-center gap-2 px-4 py-2 text-sm",
                                    active ? "bg-neutral-100 dark:bg-neutral-800" : "",
                                    "text-neutral-700 dark:text-neutral-200",
                                  ].join(" ")}
                                >
                                  <PencilSimple size={16} />
                                  Edit
                                </Link>
                              )}
                            </MenuItem>
                          )}

                          {checkAccess("users", "delete") && (
                            <MenuItem>
                              {({ active }) => (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setUserId(user.id)
                                    setDeleteModal(true)
                                  }}
                                  className={[
                                    "w-full flex items-center gap-2 px-4 py-2 text-sm text-rose-600",
                                    active ? "bg-neutral-100 dark:bg-neutral-800" : "",
                                  ].join(" ")}
                                >
                                  <Trash size={16} />
                                  Delete
                                </button>
                              )}
                            </MenuItem>
                          )}
                        </MenuItems>
                      </Transition>
                    </Menu>
                  )}
                </div>

                <div className="mt-4 space-y-2 text-start">
                  <div className="text-xs text-neutral-500 dark:text-neutral-400">Phone</div>
                  <div className="text-sm text-neutral-900 dark:text-white">{user.phone || "—"}</div>

                  <div className="flex items-center justify-between gap-2 pt-2">
                    <div className="inline-flex items-center gap-2 bg-neutral-100 dark:bg-neutral-800/60 rounded-full px-3 py-1 text-xs text-neutral-900 dark:text-white w-fit">
                      <UserIcon size={14} className="text-neutral-500" />
                      {user?.role?.name || "—"}
                    </div>

                    <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusClass(user.etat)}`}>
                      {String(user.etat || "active").toLowerCase()}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {!currentUsers.length && (
            <div className="mt-10 text-center text-sm text-neutral-500 dark:text-neutral-400">
              No users found.
            </div>
          )}
        </div>

        {/* Pagination */}
        <div className="mt-4 flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
          <p className="text-sm text-neutral-700 dark:text-neutral-400">
            Showing {filteredUsers.length ? indexOfFirstUser + 1 : 0} to{" "}
            {Math.min(indexOfLastUser, filteredUsers.length)} of {filteredUsers.length} users
          </p>

          <div className="flex flex-wrap gap-2">
            {Array.from({ length: pagesCount }, (_, i) => (
              <button
                key={i}
                onClick={() => paginate(i + 1)}
                className={[
                  "px-3 py-1 text-sm font-medium rounded-xl border",
                  safePage === i + 1
                    ? "bg-[#2B2B35] text-white border-[#2B2B35]"
                    : "bg-white dark:bg-neutral-900 text-neutral-700 dark:text-neutral-300 border-neutral-200 dark:border-neutral-800 hover:bg-neutral-100 dark:hover:bg-neutral-800",
                ].join(" ")}
              >
                {i + 1}
              </button>
            ))}
          </div>
        </div>

        <DeleteModal
          setDeleteId={setUserId}
          open={deleteModal}
          setOpen={setDeleteModal}
          DeleteId={userId}
          Model="users"
          setDone={setDone}
          setLoding={setLoding}
          done={done}
        />
      </motion.div>
    </div>
  )
}

export default Users
